import moduleUrl from "@reader-module/resume_reader_bg.wasm?url";
import type { ReaderJob, ReaderReply } from "./resumeReader.worker";
import workerUrl from "./resumeReader.worker?worker&url";

/**
 * Reads a picked file with the resume service's own parser, in a worker of its
 * own (resumeReader.worker.ts), so the file never leaves the browser and the
 * page never stops. The module is a static file of this site, fetched once, on
 * the first pick. The bytes are handed over, as the worker can fetch nothing.
 */

/** How long a read may take, and how long the module may take to arrive. */
export const READ_TIMEOUT_MS = 15_000;

const NO_ANSWER: ReaderReply = { unreadable: "no-answer" };

let fetched: Promise<ArrayBuffer> | null = null;

function moduleBytes(): Promise<ArrayBuffer> {
    fetched ??= fetch(moduleUrl, { signal: AbortSignal.timeout(READ_TIMEOUT_MS) }).then(
        (response) => {
            if (!response.ok) throw new Error(`the reader module answered ${response.status}`);
            return response.arrayBuffer();
        },
    );
    // A failed fetch is not kept: the next pick asks again.
    fetched.catch(() => {
        fetched = null;
    });
    return fetched;
}

/**
 * Whether to start at all. A browser with `toSorted` is new enough to know the worker
 * policy's `wasm-unsafe-eval` (Chrome 110, Firefox 115, Safari 16), and an older one must
 * not be sent that policy: it would log the source it cannot read, and then a refusal.
 */
function canRead(): boolean {
    return (
        typeof WebAssembly === "object" &&
        typeof Worker === "function" &&
        typeof Array.prototype.toSorted === "function"
    );
}

/** One worker for one job, gone once it answers, fails, runs out of time or is stopped. */
function inWorker(job: ReaderJob, stopped: AbortSignal): Promise<ReaderReply> {
    return new Promise((settle) => {
        const worker = new Worker(workerUrl, { type: "module" });
        const stop = () => finish(NO_ANSWER);
        const finish = (reply: ReaderReply) => {
            clearTimeout(timer);
            stopped.removeEventListener("abort", stop);
            worker.terminate();
            settle(reply);
        };
        const timer = setTimeout(stop, READ_TIMEOUT_MS);
        stopped.addEventListener("abort", stop, { once: true });
        worker.onmessage = (event: MessageEvent<ReaderReply>) => finish(event.data);
        worker.onerror = (event) => {
            event.preventDefault();
            stop();
        };
        worker.onmessageerror = stop;
        worker.postMessage(job, [job.module, job.file]);
    });
}

/**
 * What `file` reads to. Never rejects: every failure is `unreadable`. `stopped` ends the
 * read and its worker at once, for a person who has moved on.
 */
export async function readResumeFile(file: File, stopped: AbortSignal): Promise<ReaderReply> {
    if (!canRead()) return NO_ANSWER;
    try {
        const [module, bytes] = await Promise.all([moduleBytes(), file.arrayBuffer()]);
        if (stopped.aborted) return NO_ANSWER;
        // A copy: what is sent is gone from here, and the next file needs it too.
        return await inWorker({ module: module.slice(0), file: bytes, name: file.name }, stopped);
    } catch {
        return NO_ANSWER;
    }
}
